import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { submitEnquiry } from "@/lib/api";

const inputCls =
  "w-full bg-cream border border-chocolate/15 px-4 py-3 text-sm text-chocolate placeholder:text-chocolate/35 focus:outline-none focus:border-gold transition-colors rounded-none";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-chocolate/60 mb-2";

const SERVICES = [
  "Luxury & high-value gift",
  "Personal shopping",
  "Executive / corporate gifting",
  "Event gifts & goodie bags",
  "Last-minute gifting",
  "Rare or hard-to-find item",
  "Urgent sourcing & delivery",
  "Something else",
];

export default function ConciergeForm() {
  const [sending, setSending] = useState(false);
  const [data, setData] = useState({ name: "", email: "", phone: "", service: "", deadline: "", budget: "", details: "" });
  const set = (key) => (e) => setData((d) => ({ ...d, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name.trim() || !data.email.trim() || !data.details.trim()) {
      toast.error("Please complete your name, email and a short brief.");
      return;
    }
    setSending(true);
    try {
      await submitEnquiry("concierge", data);
      toast.success("Your concierge enquiry is with us — expect a discreet, personal reply shortly.");
      setData({ name: "", email: "", phone: "", service: "", deadline: "", budget: "", details: "" });
    } catch {
      toast.error("Something went wrong. Please try again or email Salem-sabrina-thegiftedit@outlook.com.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="concierge-enquiry-form" className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls} htmlFor="cf-name">Your name *</label>
          <input id="cf-name" data-testid="concierge-name" className={inputCls} value={data.name} onChange={set("name")} placeholder="Full name" />
        </div>
        <div>
          <label className={labelCls} htmlFor="cf-email">Email *</label>
          <input id="cf-email" data-testid="concierge-email" type="email" className={inputCls} value={data.email} onChange={set("email")} placeholder="you@email.com" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls} htmlFor="cf-phone">Phone</label>
          <input id="cf-phone" data-testid="concierge-phone" type="tel" className={inputCls} value={data.phone} onChange={set("phone")} placeholder="+44 …" />
        </div>
        <div>
          <label className={labelCls} htmlFor="cf-service">What do you need?</label>
          <select id="cf-service" data-testid="concierge-service" className={inputCls} value={data.service} onChange={set("service")}>
            <option value="">Select a service…</option>
            {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className={labelCls} htmlFor="cf-deadline">Needed by</label>
          <input id="cf-deadline" data-testid="concierge-deadline" className={inputCls} value={data.deadline} onChange={set("deadline")} placeholder="e.g. Tomorrow morning, 14th June…" />
        </div>
        <div>
          <label className={labelCls} htmlFor="cf-budget">Indicative budget</label>
          <select id="cf-budget" data-testid="concierge-budget" className={inputCls} value={data.budget} onChange={set("budget")}>
            <option value="">Select…</option>
            {["Under £500", "£500 – £1,500", "£1,500 – £5,000", "£5,000 – £15,000", "£15,000+", "Prefer to discuss"].map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor="cf-details">Your brief *</label>
        <textarea id="cf-details" data-testid="concierge-details" rows={5} className={inputCls} value={data.details} onChange={set("details")}
          placeholder="Tell us what you need, who it's for, and anything that matters — the more colour, the better." />
      </div>
      <button
        type="submit"
        data-testid="concierge-submit-button"
        disabled={sending}
        className="inline-flex items-center gap-2 bg-chocolate text-cream px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-espresso transition-colors disabled:opacity-60"
      >
        {sending ? "Sending…" : "Send Private Enquiry"} <ArrowRight size={14} />
      </button>
      <p className="text-[11px] text-chocolate/45 leading-relaxed">
        Handled in strict confidence. We will explore legitimate sourcing options, availability and timescales, and respond with an honest proposal before any commitment.
      </p>
    </form>
  );
}
